/**
 * API della vista "Batch Edits": product-service GET /batch-edits* (lettura) e
 * POST /batch-edits/:id/force-run (esecuzione forzata di un batch in attesa di conferma).
 */
import { createServiceClient } from '../../api/client.js';
import type {
  BatchDetail,
  BatchSummary,
  ForceRunResponse,
  RecordsPage,
  RecordsQuery,
} from './types.js';

const productApi = createServiceClient('product');

/** GET /batch-edits?limit=<n> — batch più recenti per primi. */
export function fetchBatches(limit = 200): Promise<BatchSummary[]> {
  return productApi<BatchSummary[]>(`/batch-edits?limit=${limit}`);
}

/** GET /batch-edits/:id — stato, statistiche, timeline delle chiamate, chunk. */
export function fetchBatch(batchId: string): Promise<BatchDetail> {
  return productApi<BatchDetail>(`/batch-edits/${encodeURIComponent(batchId)}`);
}

/** Nome del file scaricato: stessa regola del Content-Disposition di product-service. */
export function rawPayloadFilename(batchId: string): string {
  const safe = batchId.replace(/[^A-Za-z0-9._-]+/g, '_').slice(0, 100) || 'batch';
  return `batch-edit-${safe}-raw-payload.json`;
}

/**
 * GET /batch-edits/:id/raw-payload — copia RAW dei chunk ricevuti dall'API (body HTTP tal quale,
 * prima della normalizzazione), scaricata come un unico file JSON.
 */
export async function downloadBatchRawPayload(batchId: string): Promise<void> {
  const blob = await productApi<Blob>(`/batch-edits/${encodeURIComponent(batchId)}/raw-payload`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = rawPayloadFilename(batchId);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** GET /batch-edits/:id/records — record del piano con esito, filtrati e paginati. */
export function fetchBatchRecords(batchId: string, query: RecordsQuery): Promise<RecordsPage> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== '') qs.set(k, String(v));
  }
  return productApi<RecordsPage>(`/batch-edits/${encodeURIComponent(batchId)}/records?${qs}`);
}

/**
 * POST /batch-edits/:id/force-run — esegue un batch `on-hold` ignorando la soglia (rivalida sul
 * catalogo corrente e lo mette nella coda normale). Solo PORTFOLIO_MANAGEMENT; 409 se il batch
 * non è più in attesa o il payload non è più valido.
 */
export function forceRunBatch(batchId: string): Promise<ForceRunResponse> {
  return productApi<ForceRunResponse>(`/batch-edits/${encodeURIComponent(batchId)}/force-run`, {
    method: 'POST',
  });
}

/** Soglia di hold in vigore (GET /admin/batch-edit-settings, VIEW_PORTFOLIO). */
export interface HoldThresholdSettings {
  holdThreshold: number;
  source: 'admin' | 'env' | 'default';
  defaultHoldThreshold: number;
  min: number;
  max: number;
  rule: string;
  updatedAt?: string;
  updatedBy?: string;
}

export function fetchHoldThreshold(): Promise<HoldThresholdSettings> {
  return productApi<HoldThresholdSettings>('/admin/batch-edit-settings');
}
